import React from 'react';

const Button = ({ 
  children, 
  onClick, 
  type = 'button', 
  variant = 'primary', 
  size = 'md',
  disabled = false, 
  loading = false,
  className = '',
  icon = null,
  style = {},
  ...props
}) => {
  const paddings = { sm: '6px 12px', md: '10px 20px', lg: '12px 24px' };
  const fontSizes = { sm: '12px', md: '14px', lg: '15px' };

  // `style` must be destructured out of props. It previously stayed in ...props,
  // which JSX spreads AFTER style={finalStyle} on the element below - so a
  // caller passing style={{marginBottom:12}} silently replaced the ENTIRE
  // computed style, losing padding, radius and the variant background. Those
  // buttons could never follow the theme. It is now merged last instead, so a
  // caller can still override individual properties.
  const baseStyle = {
    padding: paddings[size] || paddings.md,
    borderRadius: 'var(--radius-md)',
    border: '1px solid transparent',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontSize: fontSizes[size] || fontSizes.md,
    fontWeight: '700',
    transition: 'all 0.2s ease',
    opacity: disabled || loading ? 0.6 : 1,
    whiteSpace: 'nowrap',
    outline: 'none',
  };

  const variantStyles = {
    primary: {
      background: 'var(--gradient-brand)',
      color: 'var(--color-accent-contrast)',
      boxShadow: 'var(--shadow-accent)'
    },
    secondary: {
      backgroundColor: 'var(--color-surface-muted)',
      color: 'var(--color-text-secondary)',
    },
    danger: {
      backgroundColor: 'var(--color-danger)',
      color: 'var(--color-text-inverse)',
    },
    success: {
      backgroundColor: 'var(--color-success)',
      color: 'var(--color-text-inverse)',
    },
    warning: {
      backgroundColor: 'var(--color-warning-hover)',
      color: 'var(--color-text-inverse)',
    }
  };

  const hoverStyles = {
    primary: {
      background: 'var(--gradient-brand-hover)',
      boxShadow: 'var(--shadow-accent-hover)'
    },
    secondary: {
      backgroundColor: 'var(--color-surface-muted-hover)',
    },
    danger: {
      backgroundColor: 'var(--color-danger-hover)',
    },
    success: {
      backgroundColor: 'var(--color-success-hover)',
    },
    warning: {
      backgroundColor: 'var(--color-warning-fg)',
    }
  };

  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  // Variants other than `primary` carry no box-shadow, so the previous
  // unconditional concatenation produced a leading comma (", 0 0 0 3px ...")
  // — invalid CSS, which meant those buttons rendered no focus ring at all.
  const focusRing = '0 0 0 3px var(--color-accent-ring)';
  const restingShadow = hoverStyles[variant]?.boxShadow || variantStyles[variant]?.boxShadow || '';

  const finalStyle = {
    ...baseStyle,
    ...variantStyles[variant],
    ...(isHovered && !disabled && !loading ? hoverStyles[variant] : {}),
    ...(isFocused && !disabled && !loading
      ? { boxShadow: restingShadow ? `${restingShadow}, ${focusRing}` : focusRing }
      : {}),
    ...style   // caller overrides win, but only for the properties they set
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={className}
      style={finalStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      {...props}
    >
      {loading && (
        <div style={{
          width: '16px',
          height: '16px',
          border: '2px solid transparent',
          borderTop: '2px solid currentColor',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      )}
      {icon && !loading && icon}
      {children}
    </button>
  );
};

export default Button;